"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

const STAFF = ["Maria", "James", "Carlos", "Sofia", "Alex"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Mock schedule
const SHIFTS: Record<string, Array<{ start: string; end: string; hot?: boolean }>> = {
  Maria: [{ start: "11", end: "8" }, { start: "11", end: "8" }, { start: "11", end: "8" }, { start: "11", end: "8" }, { start: "11", end: "8" }, { start: "OFF", end: "" }, { start: "OFF", end: "" }],
  James: [{ start: "OFF", end: "" }, { start: "OFF", end: "" }, { start: "4", end: "11" }, { start: "4", end: "11" }, { start: "4", end: "11" }, { start: "4", end: "11", hot: true }, { start: "4", end: "11" }],
  Carlos: [{ start: "11", end: "4" }, { start: "11", end: "4" }, { start: "OFF", end: "" }, { start: "OFF", end: "" }, { start: "11", end: "4" }, { start: "11", end: "4" }, { start: "11", end: "4" }],
  Sofia: [{ start: "5", end: "11" }, { start: "5", end: "11" }, { start: "5", end: "11" }, { start: "OFF", end: "" }, { start: "5", end: "11" }, { start: "5", end: "11" }, { start: "OFF", end: "" }],
  Alex: [{ start: "OFF", end: "" }, { start: "4", end: "11" }, { start: "4", end: "11" }, { start: "4", end: "11" }, { start: "OFF", end: "" }, { start: "4", end: "11" }, { start: "4", end: "11" }],
};

export default function TimeClockSchedule() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Weekly schedule", "Horario semanal")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Build the week. ", "Arma la semana. ")}
            <span className="text-redy-charcoal/30">{t("See the hours.", "Ve las horas.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Drag-drop schedule builder. Lay out every shift for the week and see the total hours at a glance, before the week starts.",
              "Schedule con drag-drop. Arma cada turno de la semana y ve las horas totales de un vistazo, antes de que empiece la semana."
            )}
          </p>
        </motion.div>

        {/* Schedule grid */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-[#FAFAF8] border border-black/5 rounded-[24px] p-5 md:p-7 max-w-[960px] mx-auto overflow-x-auto"
        >
          <div className="min-w-[640px]">
            <div className="flex items-baseline justify-between mb-5">
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                {t("Week of Sep 8", "Semana del 8 Sep")}
              </div>
              <div className="text-[10px] font-bold text-redy-secondary">
                5 {t("staff", "personas")} · 132 {t("hours scheduled", "hrs programadas")}
              </div>
            </div>

            {/* Header */}
            <div className="grid grid-cols-[80px,repeat(7,1fr)] gap-1 mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60"></div>
              {DAYS.map((d) => (
                <div
                  key={d}
                  className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60 text-center pb-1"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Staff rows */}
            <div className="space-y-1">
              {STAFF.map((person, i) => (
                <motion.div
                  key={person}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: 0.1 + 0.08 * i }}
                  className="grid grid-cols-[80px,repeat(7,1fr)] gap-1 items-stretch"
                >
                  <div className="flex items-center text-[11px] font-bold text-redy-charcoal">
                    {person}
                  </div>
                  {SHIFTS[person].map((shift, di) => {
                    const off = shift.start === "OFF";
                    return (
                      <div
                        key={di}
                        className={`rounded-[6px] py-2 text-center text-[9px] font-bold ${
                          off ? "text-redy-secondary/40" : "text-white"
                        }`}
                        style={{
                          backgroundColor: off
                            ? "rgba(0,0,0,0.04)"
                            : shift.hot
                              ? "#D7263D"
                              : "#F4722B",
                        }}
                      >
                        {off ? "OFF" : `${shift.start}-${shift.end}`}
                      </div>
                    );
                  })}
                </motion.div>
              ))}
            </div>

            {/* Footer summary */}
            <div className="mt-5 pt-4 border-t border-black/5 grid grid-cols-3 gap-3 text-[11px]">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/55 mb-0.5">
                  {t("Total hours", "Horas totales")}
                </div>
                <div className="text-[14px] font-black text-redy-charcoal tabular-nums">132</div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/55 mb-0.5">
                  {t("Est. labor cost", "Costo de mano de obra")}
                </div>
                <div className="text-[14px] font-black text-redy-charcoal tabular-nums">$3,420</div>
              </div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-redy-secondary/55 mb-0.5">
                  {t("OT projected", "OT proyectado")}
                </div>
                <div className="text-[14px] font-black tabular-nums" style={{ color: "#F4722B" }}>
                  4 hrs
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
